import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  graphPurpleColor,
  graphOrangeColor,
} from '../../../styling/stylingHelper';
import {
  getAdminSummaryStats,
  getAdminUserStats,
  getAdminOrderStats,
  getAdminRevenueStats,
  AdminSummaryStats,
  AdminUserStats,
  AdminOrderStats,
  AdminRevenueStats,
} from '../../../services/admin/adminService';
import { getUserIdsWithSetting } from '../../../services/admin/superAdminSettingsService';
import { AdminLayout } from './AdminLayout';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [summaryStats, setSummaryStats] = useState<AdminSummaryStats | null>(null);
  const [userStats, setUserStats] = useState<AdminUserStats | null>(null);
  const [orderStats, setOrderStats] = useState<AdminOrderStats | null>(null);
  const [revenueStats, setRevenueStats] = useState<AdminRevenueStats | null>(null);
  const [demoAccountCount, setDemoAccountCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [summary, users, orders, revenue, demoIds] = await Promise.all([
          getAdminSummaryStats(),
          getAdminUserStats(),
          getAdminOrderStats(),
          getAdminRevenueStats(),
          getUserIdsWithSetting('demo_account'),
        ]);
        setSummaryStats(summary);
        setUserStats(users);
        setOrderStats(orders);
        setRevenueStats(revenue);
        setDemoAccountCount(demoIds.length);
      } catch (error) {
        console.error('Failed to load admin stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  const totalRevenue = revenueStats?.totalRevenueTracked ?? 0;
  const totalSpent = orderStats?.ordersByPlatform.reduce((sum, p) => sum + p.totalSpent, 0) ?? 0;

  return (
    <AdminLayout>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Total Users"
          value={summaryStats?.totalUsers ?? 0}
          subtext={`${userStats?.activeSubscriptions ?? 0} active subscriptions`}
        />
        <MetricCard
          label="Total Orders"
          value={summaryStats?.totalOrders ?? 0}
          subtext={`${summaryStats?.uniquePlatforms ?? 0} platforms`}
        />
        <MetricCard
          label="Total Spend Tracked"
          value={formatCurrency(totalSpent)}
          subtext="across all users"
        />
        <MetricCard
          label="Revenue Tracked"
          value={formatCurrency(totalRevenue)}
          subtext={`${revenueStats?.totalRevenueEntries ?? 0} entries`}
        />
      </div>

      {/* Section Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Section */}
        <SectionCard
          title="Users"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          onClick={() => navigate('/admin/users')}
        >
          <div className="space-y-3">
            <StatRow label="Admin accounts" value={userStats?.adminUsers.length ?? 0} />
            <StatRow label="Staff users" value={userStats?.adminUsers.reduce((sum, a) => sum + a.staff.length, 0) ?? 0} />
            <StatRow label="Active subscriptions" value={userStats?.activeSubscriptions ?? 0} />
            <StatRow
              label="Demo accounts"
              value={demoAccountCount}
              muted
            />
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
              <StatRow
                label="Total real users"
                value={userStats?.totalUsers ?? 0}
                highlight
              />
            </div>
          </div>
        </SectionCard>

        {/* Orders Section */}
        <SectionCard
          title="Orders"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
          onClick={() => navigate('/admin/orders')}
        >
          <div className="space-y-3">
            <StatRow label="Total orders" value={(orderStats?.totalOrders ?? 0).toLocaleString()} />
            <StatRow label="Platforms" value={orderStats?.uniquePlatforms.length ?? 0} />
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
              <StatRow
                label="Total spent"
                value={formatCurrency(totalSpent)}
                highlight
              />
            </div>
            {/* Mini bar chart preview */}
            <div className="pt-2">
              <div className={cn(secondaryText, 'text-xs mb-2')}>Top platforms</div>
              {orderStats?.ordersByPlatform
                .sort((a, b) => b.orderCount - a.orderCount)
                .slice(0, 3)
                .map(p => (
                  <div key={p.platform} className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(p.orderCount / (orderStats?.totalOrders || 1)) * 100}%`,
                          backgroundColor: graphPurpleColor,
                        }}
                      />
                    </div>
                    <span className={cn('text-xs', secondaryText, 'w-20 truncate')}>
                      {p.platform}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </SectionCard>

        {/* Revenue Section */}
        <SectionCard
          title="Revenue"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          onClick={() => navigate('/admin/revenue')}
        >
          <div className="space-y-3">
            <StatRow label="Revenue entries" value={(revenueStats?.totalRevenueEntries ?? 0).toLocaleString()} />
            <StatRow label="Source types" value={revenueStats?.revenueBySourceType.length ?? 0} />
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
              <StatRow
                label="Total revenue"
                value={formatCurrency(totalRevenue)}
                highlight
              />
            </div>
            {/* Mini bar chart preview */}
            <div className="pt-2">
              <div className={cn(secondaryText, 'text-xs mb-2')}>By source</div>
              {revenueStats?.revenueBySourceType
                .sort((a, b) => b.totalRevenue - a.totalRevenue)
                .slice(0, 3)
                .map(s => (
                  <div key={s.sourceType} className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(s.totalRevenue / (totalRevenue || 1)) * 100}%`,
                          backgroundColor: graphOrangeColor,
                        }}
                      />
                    </div>
                    <span className={cn('text-xs', secondaryText, 'w-20 truncate')}>
                      {s.sourceType}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </SectionCard>
      </div>
    </AdminLayout>
  );
};

// Metric Card Component
const MetricCard = ({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string | number;
  subtext: string;
}) => (
  <div className={cn(cardClass, 'p-4')}>
    <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>{label}</div>
    <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
    <div className={cn(secondaryText, 'text-xs mt-1')}>{subtext}</div>
  </div>
);

// Section Card Component
const SectionCard = ({
  title,
  icon,
  children,
  onClick,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <div
    className={cn(
      cardClass,
      'p-5 cursor-pointer transition-all hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800'
    )}
    onClick={onClick}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="text-purple-600 dark:text-purple-400">{icon}</div>
        <h3 className={cn(primaryText, semiBoldText)}>{title}</h3>
      </div>
      <svg
        className="w-4 h-4 text-gray-400 dark:text-textQuaternary-dark"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
    {children}
  </div>
);

// Stat Row Component
const StatRow = ({
  label,
  value,
  highlight = false,
  muted = false,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
  muted?: boolean;
}) => (
  <div className="flex items-center justify-between">
    <span className={cn(smallTextSize, muted ? 'text-gray-400 dark:text-textQuaternary-dark' : secondaryText)}>
      {label}
    </span>
    <span
      className={cn(
        smallTextSize,
        semiBoldText,
        highlight ? 'text-purple-600 dark:text-purple-400' : muted ? 'text-gray-400' : primaryText
      )}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </span>
  </div>
);

export default AdminDashboardPage;
